import React, { useState } from "react";
import { motion } from "framer-motion";
import LegalModal from "../components/LegalModal";

export default function InsightsSection() {
  const [openId, setOpenId] = useState(null);

  // Each article follows the same sales-minded flow:
  // 1) a real problem we actually deal with,
  // 2) how we solved it / what we built,
  // 3) a clear nudge to get in touch.
  const articles = [
    {
      id: "ssl-expiry",
      tag: "We solved this",
      color: "#00ff9d",
      icon: "🔒",
      title: "An expired SSL certificate took a site down — so we fixed it for good",
      summary:
        "We've seen sites go offline because an SSL certificate quietly expired. So we built automatic SSL tracking that warns you before it ever happens.",
      readTime: "3 min read",
      body: (
        <>
          <p>
            Here's a problem we keep running into: a website suddenly starts showing
            visitors a scary <strong>"Not Secure"</strong> warning, or stops loading
            altogether. The cause is almost always the same—an SSL certificate
            quietly expired and nobody noticed.
          </p>
          <h4>Why this keeps happening</h4>
          <p>
            SSL certificates don't last forever—most are valid for only 90 days to a
            year. Nothing reminds you before they expire, and the person who set it
            up may have moved on. Teams usually find out the worst way: a customer
            complains that the site is broken.
          </p>
          <h4>What it costs you</h4>
          <p>
            An expired certificate means lost visitors, lost sales, and lost
            trust—often for hours before anyone realises. For a business, that's
            real money gone over something completely avoidable.
          </p>
          <h4>How we solved it</h4>
          <p>
            We faced this ourselves, so we built SSL tracking into our monitoring
            platform. It checks your certificates automatically and emails you{" "}
            <strong>30, 15, and 3 days</strong> before expiry—so renewal becomes a
            calm, planned task instead of an emergency.
          </p>
          <p>
            If you'd rather never worry about an expired certificate again, this is
            exactly the kind of thing we set up for businesses. Let's talk.
          </p>
        </>
      ),
    },
    {
      id: "downtime-cost",
      tag: "We solved this",
      color: "#00d4ff",
      icon: "📉",
      title: "We got tired of finding out about downtime from customers",
      summary:
        "Finding out your site or API is down from an angry customer is the worst way to learn it. We built monitoring that tells us first — in seconds.",
      readTime: "3 min read",
      body: (
        <>
          <p>
            One thing we learned the hard way: the worst way to discover your website
            or app is down is when a customer messages you about it. By then it may
            have been down for hours, and you have no idea why.
          </p>
          <h4>Downtime is a business problem, not just a tech one</h4>
          <p>
            Every minute your site or API is unreachable, you may be losing sales and
            leads. Search engines can lower your ranking if your site is often down,
            and customers who hit an error rarely come back to try again.
          </p>
          <h4>How we solved it</h4>
          <p>
            We built monitoring that watches websites, APIs, and databases (MySQL,
            Redis, MongoDB) around the clock. The moment something goes down, we get
            an alert in seconds—so we can act in minutes, not hours. It also tracks
            response times, so slowdowns get caught before they become outages.
          </p>
          <p>
            If you want to be the first to know when something breaks—not the
            last—this is something we can set up for your business too. Reach out and
            we'll walk you through it.
          </p>
        </>
      ),
    },
    {
      id: "custom-software",
      tag: "Can we help?",
      color: "#ff6b35",
      icon: "🧩",
      title: "Not sure if you need custom software? Here's how we help you decide",
      summary:
        "Off-the-shelf tools are great—until they hold you back. These are the signs we look for when helping a business decide to build something custom.",
      readTime: "4 min read",
      body: (
        <>
          <p>
            A question we get a lot: "Do we really need custom software, or is a
            ready-made tool enough?" Ready-made is a smart start—but as a business
            grows, generic tools often start getting in the way. Here are the signs
            we look for.
          </p>
          <h4>1. You're paying for features you don't use</h4>
          <p>
            If you're stuck in a big expensive plan just to get a couple of features,
            custom software can give you exactly what you need—nothing more.
          </p>
          <h4>2. Your team juggles too many disconnected tools</h4>
          <p>
            Work scattered across spreadsheets, chat apps, and five subscriptions
            wastes time. One tool built for your workflow removes that friction.
          </p>
          <h4>3. You're doing the same manual work every day</h4>
          <p>
            Repetitive tasks—copying data, manual reports—are a sign automation (and
            often AI) could save you real hours.
          </p>
          <h4>4. Your tools can't grow with you</h4>
          <p>
            If things slow down or break as you add users or data, you've outgrown
            them. We build software to scale from the start.
          </p>
          <h4>5. You have an idea no existing tool covers</h4>
          <p>
            Sometimes what you need simply doesn't exist yet—and that can become a
            real advantage over competitors.
          </p>
          <p>
            If a few of these sound familiar, it's worth a quick chat. We'll help you
            figure out whether custom software is the right move—honestly, even if the
            answer is "not yet."
          </p>
        </>
      ),
    },
  ];

  const active = articles.find((a) => a.id === openId);

  return (
    <section className="insights" id="insights">
      <h2>Real Problems We Solve</h2>
      <p className="section-subtitle">
        Things we've actually faced—and the solutions we built. If any of these sound
        familiar, let's talk.
      </p>

      <div className="insights-grid">
        {articles.map((a, i) => (
          <motion.article
            key={a.id}
            className="insight-card"
            style={{ "--card-color": a.color }}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            whileHover={{ y: -8, boxShadow: `0 24px 55px ${a.color}29` }}
          >
            <div className="insight-top">
              <span
                className="insight-icon"
                style={{
                  background: `linear-gradient(135deg, ${a.color}, ${a.color}99)`,
                  boxShadow: `0 8px 22px ${a.color}55`,
                }}
              >
                {a.icon}
              </span>
              <span
                className="insight-tag"
                style={{
                  color: a.color,
                  background: `${a.color}1f`,
                  borderColor: `${a.color}4d`,
                }}
              >
                {a.tag}
              </span>
            </div>

            <h3>{a.title}</h3>
            <p className="insight-summary">{a.summary}</p>

            <div className="insight-footer">
              <span className="insight-readtime">{a.readTime}</span>
              <button
                className="insight-read-btn"
                style={{ color: a.color }}
                onClick={() => setOpenId(a.id)}
              >
                Read more →
              </button>
            </div>

            {/* Full article kept in the DOM (visually hidden) so search engines
                can crawl and index the complete content, even while the card
                only shows a summary. The modal shows the same text on click. */}
            <div className="insight-seo-body" aria-hidden="true">
              {a.body}
            </div>
          </motion.article>
        ))}
      </div>

      <motion.div
        className="insights-cta"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <p>Facing something like this? We've probably dealt with it before.</p>
        <a href="#contact" className="insights-cta-btn">
          Let's talk →
        </a>
      </motion.div>

      <LegalModal
        open={!!active}
        onClose={() => setOpenId(null)}
        title={active ? active.title : ""}
      >
        {active ? (
          <div className="insight-article">
            {active.body}
            <a
              href="#contact"
              className="insight-cta"
              onClick={() => setOpenId(null)}
            >
              Send us an inquiry →
            </a>
          </div>
        ) : null}
      </LegalModal>
    </section>
  );
}
